import { useMemo } from "react";
import {
  summarizeSeason,
  buildRadar,
  buildCapabilities,
  buildInvolvementMap,
  buildPlayerInfo,
  buildSeasonStats,
  buildTraits,
  buildPlayerNarrative,
  leagueNameForSeason,
  calcAge,
  pickReportSeasonEntry,
  buildRadarInsight,
  buildTransferInsight,
  buildInvolvementInsight,
} from "../../../lib/playerReport";
import ReportRadarChart from "./ReportRadarChart";
import ReportTransferChart from "./ReportTransferChart";
import ReportInvolvementPitch from "./ReportInvolvementPitch";
import ReportCapabilityCards from "./ReportCapabilityCards";
import ReportPositionPitch from "./ReportPositionPitch";
import ReportProvenance from "./ReportProvenance";
import { summarizeTransfers } from "../../../lib/reportHelpers";

const INFO_ROWS = [
  { key: "club", label: "Club" },
  { key: "nationality", label: "Nationalities" },
  { key: "birthDate", label: "Birth date" },
  { key: "strongFoot", label: "Strong foot" },
];

const STAT_ROWS = [
  { key: "minutes", label: "Minutes played", format: (value) => value.toLocaleString() },
  { key: "matches", label: "Matches" },
  { key: "fromStart", label: "From start" },
  { key: "goals", label: "Goals" },
  { key: "assists", label: "Assists" },
  { key: "yellowCards", label: "Yellow cards" },
  { key: "redCards", label: "Red cards" },
];

function AiHeading({ text, fallback, status }) {
  const loading = status === "loading" && !text;
  return (
    <p className={"pr-ai-heading" + (loading ? " is-loading" : "")}>
      {text || fallback}
    </p>
  );
}

export default function PlayerReport({ data, player, seasonId, seasonName }) {
  const rawStats = useMemo(() => data?.profile?.statistics || data?.statistics || [], [data]);
  const entry = useMemo(
    () => pickReportSeasonEntry(rawStats, seasonId, seasonName),
    [rawStats, seasonId, seasonName],
  );

  const profile = data?.profile;
  const positionCode = profile?.detailedposition?.code || profile?.position?.code || "";
  const roleLabel = profile?.detailedposition?.name || profile?.position?.name || "";
  const age = player?.age ?? calcAge(profile?.date_of_birth);

  const summary = useMemo(() => (entry ? summarizeSeason(entry) : null), [entry]);

  const radarAxes = useMemo(() => {
    if (!entry || !summary) return [];
    const hasAerialData = (entry.details || []).some(
      (detail) => detail.type?.code === "aeriels-won" || detail.type?.code === "aerials-won",
    );
    const aerialWon = (entry.details || []).find(
      (detail) => detail.type?.code === "aeriels-won" || detail.type?.code === "aerials-won",
    )?.value?.total ?? 0;
    return buildRadar({ ...summary, aerialsWon: aerialWon }).filter((axis) => axis.key !== "aerial" || hasAerialData);
  }, [entry, summary]);

  const capabilities = useMemo(
    () => (entry && summary ? buildCapabilities(entry, summary) : null),
    [entry, summary],
  );

  const involvement = useMemo(() => {
    if (!entry || !summary) return null;
    const lineups = data?.profile?.lineups || data?.lineups || [];
    const seasonLineups = lineups.filter((lineup) => lineup.fixture?.season_id === entry.season?.id);
    return buildInvolvementMap(seasonLineups, positionCode, summary);
  }, [data, entry, summary, positionCode]);

  const info = useMemo(() => (data ? buildPlayerInfo(data) : null), [data]);
  const seasonStats = useMemo(() => (entry ? buildSeasonStats(entry) : null), [entry]);
  const traits = useMemo(() => buildTraits(radarAxes), [radarAxes]);
  const leagueName = useMemo(() => leagueNameForSeason(data, entry?.season?.id), [data, entry]);

  const narrative = useMemo(
    () =>
      buildPlayerNarrative({
        name: profile?.display_name,
        age,
        role: roleLabel,
        club: info?.club,
        strongFoot: info?.strongFoot,
        seasonName: summary?.seasonName,
        leagueName,
        stats: seasonStats,
        strengths: traits.strengths,
        weaknesses: traits.weaknesses,
      }),
    [profile, age, roleLabel, info, summary, leagueName, seasonStats, traits],
  );

  const transfers = useMemo(() => data?.transfers || data?.profile?.transfers || [], [data]);
  const radarAxisRows = useMemo(() => [...radarAxes].sort((a, b) => b.value - a.value), [radarAxes]);
  const radarInsight = useMemo(() => buildRadarInsight(radarAxes), [radarAxes]);
  const transferInsight = useMemo(() => buildTransferInsight(transfers), [transfers]);
  const involvementInsight = useMemo(
    () => buildInvolvementInsight(involvement, roleLabel),
    [involvement, roleLabel],
  );

  const transferFacts = useMemo(() => summarizeTransfers(transfers), [transfers]);

  const ai = null;
  const aiStatus = "idle";

  if (!entry || !summary) {
    return (
      <p className="pr-status">
        No report data available for {seasonName || "this season"}. This season may not have started yet — select a previous season to view historical data.
      </p>
    );
  }

  const overallRating = summary.rating != null ? Number(summary.rating) : null;

  return (
    <div className="player-report">
      <section className="pr-section">
        <div className="pr-report-top">
          <div className="i-card pr-card pr-narrative">
            <div className="pr-section-head pr-narrative-head">
              <h2>
                Player Report
                <span className="pr-name"> | {profile?.display_name || player?.name}</span>
              </h2>
            </div>
            {ai?.summary?.insight ? (
              <p className="pr-narrative-ai">{ai.summary.insight}</p>
            ) : (
              <>
                {narrative.lead && <p className="pr-narrative-lead">{narrative.lead}</p>}
                {narrative.body && <p>{narrative.body}</p>}
              </>
            )}
            <ReportProvenance grounding={ai?.summary?.grounding} freshness={ai?.summary?.freshness} />
          </div>
          <div className="i-card pr-card">
            <ReportPositionPitch
              code={positionCode}
              mainLabel={roleLabel || "—"}
              otherLabel={profile?.position?.name !== profile?.detailedposition?.name ? profile?.position?.name : null}
            />
          </div>
        </div>
      </section>

      <div className="pr-grid-2">
        <div className="i-card pr-card">
          <h3 className="pr-card-title">Info</h3>
          <dl className="pr-stat-list">
            {INFO_ROWS.map((row) => (
              <div key={row.key} className="pr-stat-row">
                <dt>{row.label}</dt>
                <dd>{info?.[row.key] ?? "—"}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="i-card pr-card">
          <h3 className="pr-card-title">
            Season Stats
            <span className="pr-name"> | {summary.seasonName}</span>
          </h3>
          <dl className="pr-stat-list">
            {STAT_ROWS.map((row) => {
              const value = seasonStats?.[row.key];
              return (
                <div key={row.key} className="pr-stat-row">
                  <dt>{row.label}</dt>
                  <dd>{value == null ? "—" : row.format ? row.format(value) : value}</dd>
                </div>
              );
            })}
          </dl>
        </div>
      </div>

      <div className="pr-grid-3">
        <div className="i-card pr-card pr-rating-card">
          <h3 className="pr-card-title">Overall Rating</h3>
          <div className="pr-rating-value">
            {overallRating != null ? overallRating.toFixed(1) : "—"}
            <span className="pr-rating-scale"> / 10</span>
          </div>
          <div className="pr-rating-sub">Average match rating, {summary.seasonName}</div>
        </div>
        <div className="i-card pr-card">
          <h3 className="pr-card-title">Strengths</h3>
          <p className="pr-card-hint">Highest radar fields this season</p>
          {traits.strengths.length === 0 ? (
            <div className="pr-empty">No radar data for this season.</div>
          ) : (
            <ul className="pr-trait-list">
              {traits.strengths.map((trait) => (
                <li key={trait.key} className="pr-trait-row">
                  <span className="pr-trait-label">{trait.label}</span>
                  <span className="pr-trait-value">{trait.value.toFixed(1)} / 10</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="i-card pr-card">
          <h3 className="pr-card-title">Weaknesses</h3>
          <p className="pr-card-hint">Lowest radar fields this season</p>
          {traits.weaknesses.length === 0 ? (
            <div className="pr-empty">No radar data for this season.</div>
          ) : (
            <ul className="pr-trait-list">
              {traits.weaknesses.map((trait) => (
                <li key={trait.key} className="pr-trait-row">
                  <span className="pr-trait-label">{trait.label}</span>
                  <span className="pr-trait-value">{trait.value.toFixed(1)} / 10</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <section className="pr-section">
        <header className="pr-section-head">
          <h2>
            Transfer Value
            <span className="pr-name"> | {profile?.display_name}</span>
          </h2>
          <AiHeading
            text={ai?.transfer?.heading}
            fallback="Recorded transfer fees and move history"
            status={aiStatus}
          />
        </header>
        <div className="pr-split-grid">
          <div className="i-card pr-card pr-text-card">
            <p className="pr-insight">{ai?.transfer?.insight || transferInsight}</p>
            <dl className="pr-stat-list pr-facts">
              <div className="pr-stat-row">
                <dt>Recorded moves</dt>
                <dd>{transferFacts.count}</dd>
              </div>
              <div className="pr-stat-row">
                <dt>Latest move</dt>
                <dd>{transferFacts.latestMove || "—"}</dd>
              </div>
              <div className="pr-stat-row">
                <dt>Latest value</dt>
                <dd>{transferFacts.latestValue || "—"}</dd>
              </div>
              <div className="pr-stat-row">
                <dt>Highest value</dt>
                <dd>{transferFacts.highestValue || "—"}</dd>
              </div>
            </dl>
          </div>
          <div className="i-card pr-card pr-chart-card">
            <ReportTransferChart transfers={transfers} hideSummary />
          </div>
        </div>
        <ReportProvenance grounding={ai?.transfer?.grounding} freshness={ai?.transfer?.freshness} />
      </section>

      <section className="pr-section">
        <header className="pr-section-head">
          <h2>
            Player Capability Radar
            <span className="pr-name"> | {profile?.display_name}</span>
          </h2>
          <AiHeading
            text={ai?.radar?.heading}
            fallback={`Nine capability axes scored 0–10 from ${summary.seasonName} match statistics — higher is stronger.`}
            status={aiStatus}
          />
        </header>
        <div className="pr-split-grid">
          <div className="i-card pr-card pr-text-card">
            <p className="pr-insight">{ai?.radar?.insight || radarInsight}</p>
            <dl className="pr-stat-list pr-facts">
              {radarAxisRows.map((axis) => (
                <div key={axis.key} className="pr-stat-row">
                  <dt>{axis.label}</dt>
                  <dd>{axis.value.toFixed(1)}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="i-card pr-card pr-chart-card pr-radar-card">
            {radarAxes.length > 0 && <ReportRadarChart axes={radarAxes} />}
          </div>
        </div>
        <ReportProvenance grounding={ai?.radar?.grounding} freshness={ai?.radar?.freshness} />
      </section>

      <section className="pr-section">
        <header className="pr-section-head">
          <h2>
            Formation Involvement
            <span className="pr-name"> | {profile?.display_name}</span>
          </h2>
          <AiHeading
            text={ai?.involvement?.heading}
            fallback={`Where starts came from in ${summary.seasonName}, modelled from starting lineups.`}
            status={aiStatus}
          />
        </header>
        <div className="pr-grid-2">
          <div className="i-card pr-card pr-text-card">
            <p className="pr-insight">{ai?.involvement?.insight || involvementInsight}</p>
            <dl className="pr-stat-list pr-facts">
              <div className="pr-stat-row">
                <dt>Starts mapped</dt>
                <dd>{involvement?.total ?? 0}</dd>
              </div>
              <div className="pr-stat-row">
                <dt>Appearances</dt>
                <dd>{involvement?.seasonTotals.appearances ?? 0}</dd>
              </div>
              <div className="pr-stat-row">
                <dt>Goals</dt>
                <dd>{involvement?.seasonTotals.goals ?? 0}</dd>
              </div>
              <div className="pr-stat-row">
                <dt>Assists</dt>
                <dd>{involvement?.seasonTotals.assists ?? 0}</dd>
              </div>
            </dl>
          </div>
          <div className="i-card pr-card pr-chart-card">
            {involvement && <ReportInvolvementPitch involvement={involvement} roleLabel={roleLabel} />}
          </div>
        </div>
        <ReportProvenance grounding={ai?.involvement?.grounding} freshness={ai?.involvement?.freshness} />
      </section>

      <ReportCapabilityCards
        capabilities={capabilities}
        radarAxes={radarAxes}
        playerName={profile?.display_name}
        aiCapabilities={ai?.capabilities}
      />
    </div>
  );
}
